import {
  Drawer,
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Input,
  Row,
  Col,
  Divider,
  Empty,
  Spin,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import FinanceService from "../../../dependencies/custom/financeService";

/**
 * Fee Structure Detail Drawer
 * Shows full details of a fee structure and all students with this structure
 */
const FeeStructureDetailDrawer = ({
  visible,
  onClose,
  feeStructureData,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  studentsLoading = false,
  students = [],
}) => {
  const [studentSearch, setStudentSearch] = useState("");

  const studentRows = useMemo(() => {
    if (!Array.isArray(students)) return [];

    return students.map((student, index) => ({
      ...student,
      tableRowKey:
        student.assignmentId ||
        `${student.spcCode || student.spc || "student"}-${index}`,
    }));
  }, [students]);

  if (!feeStructureData) return null;

  // Filter students by search
  const filteredStudents = studentRows.filter((student) => {
    if (!studentSearch) return true;
    const search = studentSearch.toLowerCase();
    const name = (
      student.firstName ? `${student.firstName} ${student.lastName}` : ""
    ).toLowerCase();
    const spc = (student.spcCode || student.spc || "").toLowerCase();
    const className = (
      student.className ||
      student.class_name ||
      ""
    ).toLowerCase();

    return (
      name.includes(search) ||
      spc.includes(search) ||
      className.includes(search)
    );
  });

  // Table columns for students
  const studentColumns = [
    {
      title: "Student Name",
      key: "studentName",
      render: (_, record) => {
        const firstName = record.firstName || "";
        const lastName = record.lastName || "";
        return `${firstName} ${lastName}`.trim() || "-";
      },
      width: 150,
    },
    {
      title: "SPC",
      key: "spc",
      render: (_, record) => record.spcCode || record.spc || "-",
      width: 120,
    },
    {
      title: "Class",
      key: "class",
      render: (_, record) => record.className || record.class_name || "-",
      width: 120,
    },
    {
      title: "Day/Boarding",
      key: "dayBoarding",
      render: (_, record) => {
        const type = record.dayBoarding || "-";
        return type === "-" ? (
          "-"
        ) : (
          <Tag color={type === "Boarding" ? "purple" : "cyan"}>{type}</Tag>
        );
      },
      width: 120,
    },
    {
      title: "Amount Due",
      key: "amountDue",
      render: (_, record) =>
        FinanceService.formatCurrency(record.amountDue || 0),
      align: "right",
      width: 120,
    },
    {
      title: "Paid",
      key: "amountPaid",
      render: (_, record) =>
        FinanceService.formatCurrency(record.amountPaid || 0),
      align: "right",
      width: 120,
    },
    {
      title: "Balance",
      key: "balance",
      render: (_, record) => {
        const balance = record.balance || 0;
        return (
          <span
            style={{
              fontWeight: 500,
              color: balance > 0 ? "#f5222d" : "#52c41a",
            }}
          >
            {FinanceService.formatCurrency(balance)}
          </span>
        );
      },
      align: "right",
      width: 120,
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const status = record.status || "Pending";
        const colors = {
          Pending: "orange",
          "Partially Paid": "blue",
          "Fully Paid": "green",
          Overdue: "red",
        };
        return <Tag color={colors[status] || "default"}>{status}</Tag>;
      },
      width: 120,
    },
  ];

  // Calculate statistics
  const totalStudents = studentRows.length;
  const totalRevenue = studentRows.reduce(
    (sum, student) => sum + parseFloat(student.amountDue || 0),
    0,
  );

  return (
    <Drawer
      title="Fee Structure Details"
      placement="right"
      onClose={onClose}
      open={visible}
      width={800}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <Spin spinning={studentsLoading}>
        {/* Fee Structure Information */}
        <Descriptions
          title="Fee Structure Information"
          bordered
          column={1}
          size="small"
        >
          <Descriptions.Item label="Name">
            {feeStructureData.name}
          </Descriptions.Item>
          <Descriptions.Item label="Description">
            {feeStructureData.description || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Fee Type">
            <Tag color="blue">{feeStructureData.feeTypeName}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Amount">
            {FinanceService.formatCurrency(feeStructureData.amount)}
          </Descriptions.Item>
          <Descriptions.Item label="Class">
            {feeStructureData.schoolClassName || "All Classes"}
          </Descriptions.Item>
          <Descriptions.Item label="Day/Boarding">
            <Tag
              color={
                feeStructureData.boardingType === "Boarding"
                  ? "purple"
                  : feeStructureData.boardingType === "Day"
                    ? "cyan"
                    : "blue"
              }
            >
              {feeStructureData.boardingType || "Both"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Academic Year">
            {feeStructureData.acadyr}
          </Descriptions.Item>
          <Descriptions.Item label="Mandatory">
            {feeStructureData.isMandatory ? (
              <Tag color="red">Yes</Tag>
            ) : (
              <Tag color="blue">No</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {feeStructureData.isActive === 1 ? (
              <Tag color="green">Active</Tag>
            ) : (
              <Tag color="gold">Inactive</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Created Date">
            {dayjs(feeStructureData.createdAt).format("YYYY-MM-DD HH:mm")}
          </Descriptions.Item>
          <Descriptions.Item label="Updated Date">
            {dayjs(feeStructureData.updatedAt).format("YYYY-MM-DD HH:mm")}
          </Descriptions.Item>
        </Descriptions>

        {/* Statistics Section */}
        <Divider />
        <div style={{ marginBottom: 16 }}>
          <h4>Statistics</h4>
          <Row gutter={16}>
            <Col xs={12}>
              <div
                style={{
                  backgroundColor: "#fafafa",
                  padding: 12,
                  borderRadius: 4,
                }}
              >
                <div style={{ color: "#666", fontSize: 12, marginBottom: 4 }}>
                  Total Students
                </div>
                <div style={{ fontSize: 20, fontWeight: 600 }}>
                  {totalStudents}
                </div>
              </div>
            </Col>
            <Col xs={12}>
              <div
                style={{
                  backgroundColor: "#fafafa",
                  padding: 12,
                  borderRadius: 4,
                }}
              >
                <div style={{ color: "#666", fontSize: 12, marginBottom: 4 }}>
                  Total Revenue
                </div>
                <div style={{ fontSize: 20, fontWeight: 600 }}>
                  {FinanceService.formatCurrency(totalRevenue)}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Students Section */}
        <Divider />
        <div style={{ marginBottom: 16 }}>
          <h4>Students with This Fee Structure</h4>
          <Input
            placeholder="Search by name, SPC, or class..."
            prefix={<SearchOutlined />}
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
            allowClear
            style={{ marginBottom: 16 }}
          />
          {studentRows.length > 0 ? (
            <Table
              columns={studentColumns}
              dataSource={filteredStudents}
              rowKey="tableRowKey"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          ) : (
            <Empty description="No students assigned to this fee structure" />
          )}
        </div>

        {/* Actions Section */}
        <Divider />
        <Space wrap>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => onEdit(feeStructureData)}
          >
            Edit
          </Button>

          {feeStructureData.isActive === 1 ? (
            <Button
              onClick={() => onDeactivate(feeStructureData.feeStructureId)}
            >
              Deactivate
            </Button>
          ) : (
            <Button onClick={() => onActivate(feeStructureData.feeStructureId)}>
              Activate
            </Button>
          )}

          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(feeStructureData.feeStructureId)}
          >
            Delete
          </Button>
        </Space>
      </Spin>
    </Drawer>
  );
};

export default FeeStructureDetailDrawer;
